import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Menu,
  X,
  Search,
  Linkedin,
  Twitter,
  Youtube,
  Facebook,
  ArrowRight,
  ChevronDown,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { NAV_ITEMS, SOCIAL_LINKS } from "@/data/site";
import { useScrolled } from "@/hooks/useScrollPosition";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { Logo } from "@/components/common/Logo";
import { SearchDialog } from "@/components/layout/SearchDialog";
import { EASE } from "@/lib/motion";
import { PARTNER_LINKS, SERVICE_GROUPS } from "@/data/services";

const socialIcons: Record<string, typeof Linkedin> = {
  linkedin: Linkedin,
  twitter: Twitter,
  youtube: Youtube,
  facebook: Facebook,
};

// Center links exclude Contact Us (rendered as a CTA button on the right).
const PRIMARY_LINKS = NAV_ITEMS.filter((item) => !item.path.startsWith("/contact"));

const testIdFor = (label: string) =>
  `navbar-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;

export function Navbar() {
  const scrolled = useScrolled(12);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [desktopMenu, setDesktopMenu] = useState<"services" | "partners" | null>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setDesktopMenu(null);
  }, [location.key]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Cmd/Ctrl + K opens search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const solid = scrolled || open;

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-300",
        solid
          ? "border-b border-border bg-background/90 shadow-sm backdrop-blur-md"
          : "border-b border-transparent bg-transparent",
      )}
      data-testid="navbar"
    >
      <Container className="flex h-16 items-center justify-between gap-4 lg:h-20">
        <Logo testId="navbar-logo" />

        {/* Desktop navigation */}
        <nav
          className="hidden items-center gap-0.5 xl:flex"
          aria-label="Primary"
          data-testid="navbar-desktop-nav"
        >
          {PRIMARY_LINKS.map((item) =>
            item.path === "/services" ? (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={() => setDesktopMenu("services")}
                onMouseLeave={() => setDesktopMenu(null)}
                onFocus={() => setDesktopMenu("services")}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setDesktopMenu(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setDesktopMenu(null);
                }}
              >
                <NavLink
                  to={item.path}
                  onClick={() => setDesktopMenu(null)}
                  aria-expanded={desktopMenu === "services"}
                  aria-controls="services-menu"
                  data-testid={testIdFor(item.label)}
                  className={({ isActive }) =>
                    cn(
                      "relative inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "text-primary"
                        : "text-foreground/80 hover:text-foreground",
                    )
                  }
                >
                  Services
                  <ChevronDown size={15} aria-hidden="true" />
                </NavLink>
                <div
                  id="services-menu"
                  className={cn(
                    "absolute left-1/2 top-full w-[820px] -translate-x-1/2 pt-3 transition-all",
                    desktopMenu === "services" ? "visible opacity-100" : "invisible opacity-0",
                  )}
                >
                  <div className="grid grid-cols-3 gap-6 rounded-2xl border border-border bg-background p-6 shadow-xl">
                    {SERVICE_GROUPS.map((group) => (
                      <div key={group.label}>
                        <p className="border-b border-border pb-2 text-xs font-semibold uppercase tracking-wider text-accent">
                          {group.label}
                        </p>
                        <ul className="mt-3 space-y-1">
                          {group.items.map((service) => (
                            <li key={service.id}>
                              <Link
                                to={`/services#${service.id}`}
                                onClick={() => setDesktopMenu(null)}
                                className="block rounded-lg px-2 py-2 text-sm leading-snug text-foreground/80 transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                {service.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                data-testid={testIdFor(item.label)}
                className={({ isActive }) =>
                  cn(
                    "relative rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "text-primary"
                      : "text-foreground/80 hover:text-foreground",
                  )
                }
              >
                {({ isActive }) => (
                  <span className="relative inline-flex flex-col items-center">
                    {item.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        className="absolute -bottom-1 h-0.5 w-5 rounded-full bg-primary"
                        transition={{ duration: 0.3, ease: EASE }}
                      />
                    )}
                  </span>
                )}
              </NavLink>
            ),
          )}
          <div
            className="relative"
            onMouseEnter={() => setDesktopMenu("partners")}
            onMouseLeave={() => setDesktopMenu(null)}
            onFocus={() => setDesktopMenu("partners")}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setDesktopMenu(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") setDesktopMenu(null);
            }}
          >
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
              aria-label="Partner With Us menu"
              aria-expanded={desktopMenu === "partners"}
              aria-controls="partners-menu"
              onClick={() => setDesktopMenu("partners")}
            >
              Partner With Us
              <ChevronDown size={15} aria-hidden="true" />
            </button>
            <div
              id="partners-menu"
              className={cn(
                "absolute right-0 top-full w-96 pt-3 transition-all",
                desktopMenu === "partners" ? "visible opacity-100" : "invisible opacity-0",
              )}
            >
              <div className="rounded-2xl border border-border bg-background p-4 shadow-xl">
                <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-accent">
                  Partner With Us
                </p>
                {PARTNER_LINKS.map((link) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    onClick={() => setDesktopMenu(null)}
                    className="block rounded-lg px-3 py-2.5 text-sm leading-snug text-foreground/80 transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        {/* Right cluster: socials + search + CTA */}
        <div className="hidden items-center gap-1.5 xl:flex">
          <div className="flex items-center gap-0.5">
            {SOCIAL_LINKS.map((s) => {
              const Icon = socialIcons[s.icon] ?? Linkedin;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`navbar-social-${s.icon}`}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                >
                  <Icon size={17} />
                </a>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            data-testid="navbar-search-button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
          >
            <Search size={18} />
          </button>

          <Button as="link" to="/contact#contact-form" size="sm" className="ml-1" data-testid="navbar-contact-button">
            Contact Us
          </Button>
        </div>

        {/* Mobile controls */}
        <div className="flex items-center gap-1 xl:hidden">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            data-testid="navbar-search-button-mobile"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-muted"
          >
            <Search size={20} />
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-muted"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="navbar-mobile-menu-button"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </Container>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: EASE }}
            className="overflow-hidden border-t border-border bg-background xl:hidden"
            data-testid="mobile-nav-sheet"
          >
            <Container className="flex max-h-[calc(100vh-4rem)] flex-col gap-1 overflow-y-auto py-4">
              {NAV_ITEMS.map((item) => (
                <div key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.path === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "block rounded-lg px-4 py-3 text-base font-medium transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-foreground/90 hover:bg-muted",
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                  {item.path === "/services" && (
                    <div className="ml-4 border-l border-border py-2 pl-3">
                      {SERVICE_GROUPS.map((group) => (
                        <div key={group.label} className="mb-3 last:mb-0">
                          <p className="px-2 text-xs font-semibold uppercase tracking-wider text-accent">
                            {group.label}
                          </p>
                          {group.items.map((service) => (
                            <Link
                              key={service.id}
                              to={`/services#${service.id}`}
                              onClick={() => setOpen(false)}
                              className="block rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-primary"
                            >
                              {service.title}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className="mt-2 border-t border-border px-4 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-accent">
                  Partner With Us
                </p>
                <div className="mt-2 space-y-1">
                  {PARTNER_LINKS.map((link) => (
                    <Link
                      key={link.label}
                      to={link.to}
                      onClick={() => setOpen(false)}
                      className="block rounded-lg py-2 text-sm text-foreground/80 hover:text-primary"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Social row */}
              <div className="mt-3 flex items-center gap-2 border-t border-border px-4 pt-4">
                {SOCIAL_LINKS.map((s) => {
                  const Icon = socialIcons[s.icon] ?? Linkedin;
                  return (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>

              <Button
                as="link"
                to="/contact#contact-form"
                size="lg"
                className="mt-3 w-full"
                data-testid="mobile-nav-contact-button"
              >
                Contact Us
                <ArrowRight size={18} />
              </Button>
            </Container>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
